import { useUsers } from '../api/users.api';
import { StatusLabel } from '@shared/components/StatusLabel';
import {
  AvatarGroup as AvatarGroupFluent,
  AvatarGroupItem,
  AvatarGroupPopover,
  partitionAvatarGroupItems,
  Tooltip,
  type AvatarSize,
} from '@fluentui/react-components';

import type { TMap } from '@shared/types';
import type { TUser } from '../types/users.types';

type TItemProps = {
  user?: TUser;
};
const AvatarItem = ({ user }: TItemProps) => {
  if (!user)
    return (
      // no color:
      <Tooltip withArrow relationship="description" content="User was not found.">
        <AvatarGroupItem />
      </Tooltip>
    );

  const content = `${user.name ? user.name + '\n' : ''}(${user.mail})`;
  return (
    <Tooltip withArrow relationship="description" content={content}>
      <AvatarGroupItem name={user.name} color="colorful" idForColor={user._id} />
    </Tooltip>
  );
};

const renderUsers = (userMap: TMap<string, TUser>, ids: string[], size: AvatarSize) => {
  const { inlineItems, overflowItems } = partitionAvatarGroupItems({
    items: ids,
    layout: 'stack',
    maxInlineItems: 3,
  });

  return (
    <AvatarGroupFluent layout="stack" size={size}>
      {inlineItems.map((id) => (
        <AvatarItem user={userMap[id]} key={id} />
      ))}

      {overflowItems && (
        <AvatarGroupPopover>
          {overflowItems.map((id) => (
            <AvatarItem user={userMap[id]} key={id} />
          ))}
        </AvatarGroupPopover>
      )}
    </AvatarGroupFluent>
  );
};

type TAvatarGroupContentProps = {
  ids: string[];
  userMap: TMap<string, TUser>;
  size: AvatarSize;
};

const AvatarGroupContent = ({ ids, userMap, size }: TAvatarGroupContentProps) => renderUsers(userMap, ids, size);

type TProps = {
  ids: string[];
  userMap?: TMap<string, TUser>;
  size?: AvatarSize;
};

const AvatarGroupWithFetchedUsers = ({ ids, size }: { ids: string[]; size: AvatarSize }) => {
  const { userMap, error } = useUsers();

  return (
    <StatusLabel
      data={userMap}
      loadingMessage=" "
      error={error}
      errorMessage="Failed"
      plain
      render={(userMap) => <AvatarGroupContent ids={ids} userMap={userMap} size={size} />}
    />
  );
};

const AvatarGroup = ({ ids, userMap, size = 28 }: TProps) =>
  userMap ? (
    <AvatarGroupContent ids={ids} userMap={userMap} size={size} />
  ) : (
    <AvatarGroupWithFetchedUsers ids={ids} size={size} />
  );

export default AvatarGroup;
